import { CreateButton, Show, useModal } from "@refinedev/antd";
import { Button, Space, Tag, Input, Row, Col } from "antd";
import { IResourceComponentsProps, useShow } from "@refinedev/core";
import {
  IdcardOutlined,
  MailOutlined,
  SettingOutlined,
  FileTextOutlined,
  TeamOutlined,
  DesktopOutlined,
} from "@ant-design/icons";

import { IVessel } from "interfaces";
import prettyBytes from "pretty-bytes";
import React from "react";
import {
  ShowField,
  ShowSection,
  ShowFieldGrid,
  ShowModal,
  ShowModalItem,
} from "components/show";
import { UkhoTransfersCard } from "./components/UkhoTransfersCard";

export const VesselShow: React.FC<IResourceComponentsProps> = () => {
  const { result, query: { isLoading } } = useShow<IVessel>();
  const record = result;

  const {
    modalProps: modalPropsEcdis,
    show: showEcdis,
    close: closeEcdis,
  } = useModal();
  const {
    modalProps: modalPropsContacts,
    show: showContacts,
    close: closeContacts,
  } = useModal();

  const hasEcdis = Array.isArray(record?.ecdis) && (record?.ecdis?.length ?? 0) > 0;
  const hasContacts = Array.isArray(record?.contacts) && (record?.contacts?.length ?? 0) > 0;

  return (
    <Show
      breadcrumb={false}
      isLoading={isLoading}
      headerProps={{
        onBack: () => window.history.back(),
        subTitle: "active vessel",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
      }}
      footerButtons={
        <Space>
          {!hasEcdis ? (
            <CreateButton resource="ecdis">Setup ECDIS</CreateButton>
          ) : (
            <Button type="default" onClick={showEcdis}>
              Associated ECDIS
            </Button>
          )}
          {!hasContacts ? (
            <CreateButton resource="contact">Setup Contact</CreateButton>
          ) : (
            <Button type="default" onClick={showContacts}>
              Associated Contacts
            </Button>
          )}
        </Space>
      }
    >
      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        {/* Vessel Identity */}
        <ShowSection
          title="Vessel Identity"
          subtitle="Core vessel identification details"
          icon={<IdcardOutlined />}
          variant="elevated"
        >
          <ShowFieldGrid columns={2}>
            <ShowField label="Vessel Name" value={record?.name} />
            <ShowField label="Vessel Type" value={record?.type} type="tag" tagColor="blue" />
            <ShowField label="IMO Number" value={record?.imoNumber} type="code" copyable />
            <ShowField label="Call Sign" value={record?.callSign} type="code" copyable />
            <ShowField label="UKHO Licence" value={record?.licence?.ukhoLicenceId} type="code" copyable />
            <ShowField label="Status" value={record?.holdStatus} type="status" />
          </ShowFieldGrid>
        </ShowSection>

        {/* UKHO transfers: the card checks useCan on ukho-transfer-notice and
            renders nothing, and fetches nothing, for other roles */}
        {record?.id !== undefined && (
          <UkhoTransfersCard vesselId={Number(record.id)} />
        )}

        <Row gutter={24}>
          {/* Communication */}
          <Col xs={24} lg={12}>
            <ShowSection
              title="Communication"
              subtitle="Contact information"
              icon={<MailOutlined />}
              variant="default"
            >
              <ShowFieldGrid columns={1}>
                <ShowField
                  label="Communication Email"
                  value={record?.email}
                  type="email"
                />
              </ShowFieldGrid>
            </ShowSection>
          </Col>

          {/* Transmission Settings */}
          <Col xs={24} lg={12}>
            <ShowSection
              title="Transmission Settings"
              subtitle="Size limits for data transfer"
              icon={<SettingOutlined />}
              variant="default"
            >
              <ShowFieldGrid columns={2}>
                <ShowField
                  label="Max Email Size"
                  value={record?.maxEmailSizeKb ? prettyBytes(Number(record.maxEmailSizeKb)) : null}
                />
                <ShowField
                  label="Max Transmission Size"
                  value={record?.maxTransmissionSizeMb ? prettyBytes(Number(record.maxTransmissionSizeMb) * 1024 * 1024) : null}
                />
              </ShowFieldGrid>
            </ShowSection>
          </Col>
        </Row>

        {/* EPS System IDs */}
        <ShowSection
          title="EPS System Identifiers"
          subtitle="Electronic Position System credentials"
          icon={<DesktopOutlined />}
          variant="subtle"
        >
          <ShowFieldGrid columns={2}>
            <ShowField
              label="EPS System ID (Primary)"
              value={record?.epsSystemId}
              type="code"
              copyable
            />
            <ShowField
              label="EPS System ID (Backup)"
              value={record?.epsSystemIdBackup}
              type="code"
              copyable
            />
          </ShowFieldGrid>
        </ShowSection>

        {/* Notes */}
        {record?.notes && (
          <ShowSection
            title="Notes"
            subtitle="Additional vessel information"
            icon={<FileTextOutlined />}
            variant="outlined"
          >
            <Input.TextArea
              rows={4}
              readOnly
              value={record?.notes}
              style={{ resize: "none" }}
            />
          </ShowSection>
        )}
      </Space>

      {/* ECDIS Modal */}
      <ShowModal
        title="Associated ECDIS"
        subtitle={record?.name}
        icon={<DesktopOutlined />}
        open={modalPropsEcdis.open ?? false}
        onClose={closeEcdis}
        empty={!hasEcdis}
        emptyText="No ECDIS configured for this vessel"
      >
        {record?.ecdis?.map((ecdis, index) => (
          <ShowModalItem
            key={index}
            title={`ECDIS ${index + 1}`}
            showDivider={index < (record?.ecdis?.length ?? 0) - 1}
          >
            <ShowFieldGrid columns={2}>
              <ShowField label="IMO Number" value={record?.imoNumber} type="code" />
              <ShowField label="UKHO Licence" value={record?.licence?.ukhoLicenceId} type="code" />
              <ShowField label="Make" value={ecdis?.make} />
              <ShowField label="Model" value={ecdis?.model} />
            </ShowFieldGrid>
          </ShowModalItem>
        ))}
      </ShowModal>

      {/* Contacts Modal */}
      <ShowModal
        title="Associated Contacts"
        subtitle={record?.name}
        icon={<TeamOutlined />}
        open={modalPropsContacts.open ?? false}
        onClose={closeContacts}
        empty={!hasContacts}
        emptyText="No contacts configured for this vessel"
      >
        {record?.contacts?.map((contact, index) => (
          <ShowModalItem
            key={index}
            title={`Contact ${index + 1}`}
            showDivider={index < (record?.contacts?.length ?? 0) - 1}
          >
            <ShowFieldGrid columns={2}>
              <ShowField label="Name" value={contact?.name} />
              <ShowField label="Email" value={contact?.email} type="email" />
              <ShowField label="Phone" value={contact?.phone} type="phone" />
            </ShowFieldGrid>
          </ShowModalItem>
        ))}
      </ShowModal>
    </Show>
  );
};
